import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useBatches } from 'src/views/Batches/useBatches'
import {
  RECENT_ANALYSES_KEY,
  RECENT_ANALYSES_OPTIONS,
  fetchRecentAnalyses,
} from 'src/views/NewAnalysis/useUploadedNotAnalysed'
import { historyDiffers, inProgressOf, knownRowsOf, lastFinishedOf } from './model'

// ----------------------------------------------------------------------
// The data of Home's "In progress" card: unfinished batches and running single
// analyses, both from the recent analyses useBatches already reads (one query,
// [BATCHES_QUERY_KEY]). useBatches polls every 30 s while a batch is unfinished.
// Single analyses add no timer: when Analysis History (which polls on its own)
// fetches a page showing one of them moved on, or a newer analysis, the shared
// query is refetched once.
// ----------------------------------------------------------------------

export const HISTORY_QUERY_PREFIX = 'all-analysis'

// Analysis History's key: ['all-analysis', page, limit, tags, sort_order,
// sample_name, organization_id, hidden]. The hidden list and another
// organisation's list are not the rows Home shows.
const isHomeHistoryKey = (key) => Array.isArray(key) && key[0] === HISTORY_QUERY_PREFIX && !key[6] && key[7] !== true

const useInProgress = () => {
  const queryClient = useQueryClient()
  const { batches } = useBatches()
  const query = useQuery(RECENT_ANALYSES_KEY, fetchRecentAnalyses, RECENT_ANALYSES_OPTIONS)
  const rows = useMemo(() => (Array.isArray(query.data?.results) ? query.data.results : []), [query.data])

  const progress = useMemo(() => inProgressOf(rows, batches), [rows, batches])
  const lastFinished = useMemo(() => lastFinishedOf(rows), [rows])

  const knownRef = useRef(null)
  knownRef.current = query.data ? knownRowsOf(rows) : null

  useEffect(() => {
    const cache = queryClient.getQueryCache()
    return cache.subscribe((event) => {
      if (!event || event.type !== 'queryUpdated' || event.action?.type !== 'success') return
      if (!isHomeHistoryKey(event.query?.queryKey)) return
      if (historyDiffers(event.query.state?.data?.results, knownRef.current)) {
        queryClient.invalidateQueries(RECENT_ANALYSES_KEY)
      }
    })
  }, [queryClient])

  return {
    ...progress,
    anyBatch: batches.length > 0,
    lastFinished,
    isLoading: !query.data && query.isLoading,
    isError: !query.data && query.isError,
    refetch: query.refetch,
  }
}

export default useInProgress
